// ============================================================================
// src/lib/seo/head.ts
// PV-1052 · SEO lot 1 — runtime <head> manager: per-route title, description,
// canonical, robots, Open Graph / Twitter tags and JSON-LD (CDC §14 L827).
//
// Deliberately dependency-free. Helmet-style libraries want to OWN the head,
// which would fight the static tags shipped in index.html today and the
// per-route prerendered heads planned for SEO lot 2. This module is
// UPSERT-ONLY: it reuses an existing tag when present and creates it
// otherwise, so applying the same meta over a prerendered head is a no-op
// instead of a duplication.
// ============================================================================

import { APP } from '@/config/app';

// Canonical host — intentionally NOT derived from VITE_APP_URL: a canonical
// must declare the production URL whatever host serves the document. On
// deploy previews / branch deploys, pointing the canonical at production is
// exactly the right duplicate-content signal.
export const SEO_ORIGIN = 'https://www.distressdealmarket.com';

export type SeoImage = {
  url: string;
  alt: string;
  width?: number;
  height?: number;
};

export type SeoJsonLdEntry = {
  /** Stable identity used to reconcile route-scoped blocks across navigations. */
  id: string;
  data: Record<string, unknown>;
};

export type SeoMeta = {
  title: string;
  description: string;
  /**
   * Site-relative path of the canonical URL ('/', '/faq', '/deals/<slug>').
   * Omitted → the canonical link and og:url are REMOVED: private / system
   * routes must not claim a canonical at all.
   */
  canonicalPath?: string;
  /** 'noindex' on private / auth / system routes. Omitted → tag removed. */
  robots?: 'noindex';
  /** Social preview image. Omitted → site-wide default OG image. */
  image?: SeoImage;
  /**
   * Route-scoped JSON-LD blocks, reconciled by `id` via a `data-ddm-seo`
   * marker attribute. The static RealEstateAgent block in index.html carries
   * no marker and is never touched — it is site-level, not route-level.
   */
  jsonLd?: SeoJsonLdEntry[];
};

export const DEFAULT_OG_IMAGE: SeoImage = {
  url: `${SEO_ORIGIN}/og-default.png`,
  alt: "Distress Deal Market — Dubai's distress deals before the market.",
  width: 1200,
  height: 630,
};

const JSONLD_ATTR = 'data-ddm-seo';

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeCanonical(): void {
  document.head.querySelector('link[rel="canonical"]')?.remove();
}

function reconcileJsonLd(entries: SeoJsonLdEntry[]): void {
  const wanted = new Set(entries.map((e) => e.id));
  for (const el of Array.from(document.head.querySelectorAll(`script[${JSONLD_ATTR}]`))) {
    if (!wanted.has(el.getAttribute(JSONLD_ATTR) ?? '')) el.remove();
  }
  for (const entry of entries) {
    let el = document.head.querySelector<HTMLScriptElement>(`script[${JSONLD_ATTR}="${entry.id}"]`);
    if (!el) {
      el = document.createElement('script');
      el.setAttribute('type', 'application/ld+json');
      el.setAttribute(JSONLD_ATTR, entry.id);
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(entry.data);
  }
}

/**
 * Write a route's full head state. Idempotent: re-applying the same meta
 * leaves exactly one instance of every tag. Absent optional fields actively
 * REMOVE their tags, so state never leaks from the previous route.
 */
export function applySeoMeta(meta: SeoMeta): void {
  document.title = meta.title;
  upsertMeta('name', 'description', meta.description);

  if (meta.robots) upsertMeta('name', 'robots', meta.robots);
  else removeMeta('name', 'robots');

  if (meta.canonicalPath) {
    const canonicalUrl = `${SEO_ORIGIN}${meta.canonicalPath}`;
    upsertCanonical(canonicalUrl);
    upsertMeta('property', 'og:url', canonicalUrl);
  } else {
    removeCanonical();
    removeMeta('property', 'og:url');
  }

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', APP.name);
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);

  const image = meta.image ?? DEFAULT_OG_IMAGE;
  upsertMeta('property', 'og:image', image.url);
  upsertMeta('property', 'og:image:alt', image.alt);
  if (image.width && image.height) {
    upsertMeta('property', 'og:image:width', String(image.width));
    upsertMeta('property', 'og:image:height', String(image.height));
  } else {
    // Deal covers have no persisted dimensions — stale 1200×630 values from a
    // previous route must not misdescribe them.
    removeMeta('property', 'og:image:width');
    removeMeta('property', 'og:image:height');
  }

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  upsertMeta('name', 'twitter:image', image.url);

  reconcileJsonLd(meta.jsonLd ?? []);
}
